import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Alert, Form, Button, Label, Input } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { fetchPlanProducts, searchSmrProducts, type SmrProductSearchResult } from '../services/plans';
import { usePlanProductMutations, planProductsQueryKey } from '../hooks/usePlanProductMutations';
import { PlanProductDataTable } from './PlanProductDataTable';

interface Props {
    planId: string;
}

const attachSchema = z.object({
    product_id: z.string().uuid('Select a product'),
});

type AttachInput = z.infer<typeof attachSchema>;

function useDebounce<T>(value: T, delay: number): T {
    const [debounced, setDebounced] = React.useState(value);
    React.useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

export const PlanProductPanel: React.FC<Props> = ({ planId }) => {
    const [showForm, setShowForm] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<SmrProductSearchResult | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const debouncedSearch = useDebounce(search, 300);

    const { data: attached = [], isLoading } = useQuery({
        queryKey: planProductsQueryKey(planId),
        queryFn: () => fetchPlanProducts(planId),
        enabled: Boolean(planId),
    });

    const { data: searchResults = [], isFetching: isSearching } = useQuery({
        queryKey: ['smr-admin', 'product-search', debouncedSearch],
        queryFn: () => searchSmrProducts(debouncedSearch),
        enabled: debouncedSearch.length >= 2,
    });

    const { attachMutation, detachMutation } = usePlanProductMutations(planId);

    const attachedIds = attached.map((p) => p.id);
    const filteredResults = searchResults.filter((p) => !attachedIds.includes(p.id));

    const { handleSubmit, control, setValue, reset, formState: { isSubmitting } } = useForm<AttachInput>({
        resolver: zodResolver(attachSchema),
        defaultValues: { product_id: '' },
    });

    const handleSelectProduct = (product: SmrProductSearchResult) => {
        setSelectedProduct(product);
        setValue('product_id', product.id);
        setDropdownOpen(false);
        setSearch(product.name);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setSelectedProduct(null);
        setValue('product_id', '');
        setDropdownOpen(true);
    };

    const handleAttach = () => {
        handleSubmit((data) => {
            attachMutation.mutate(data.product_id, {
                onSuccess: () => {
                    reset();
                    setSearch('');
                    setSelectedProduct(null);
                    setDropdownOpen(false);
                    setShowForm(false);
                },
            });
        })();
    };

    const handleCancel = () => {
        reset();
        setSearch('');
        setSelectedProduct(null);
        setDropdownOpen(false);
        setShowForm(false);
    };

    return (
        <div className="space-y-6">
            {isLoading ? (
                <PlanProductDataTable data={[]} onRemove={() => {}} isLoading />
            ) : attached.length === 0 ? (
                <p className="text-mp-muted text-sm">No products attached.</p>
            ) : (
                <PlanProductDataTable
                    data={attached}
                    onRemove={(productId) => detachMutation.mutate(productId)}
                    removingProductId={detachMutation.isPending ? (detachMutation.variables ?? null) : null}
                />
            )}

            <PermissionGuard permission={{ resource: 'smr', action: 'plan.edit' }}>
                <div className="border-t border-mp-border pt-6 max-w-2xl">
                    {showForm ? (
                        <Form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleAttach(); }}>
                            <div className="relative">
                                <Label htmlFor="product_search">Search Products</Label>
                                <Input
                                    id="product_search"
                                    placeholder="Type to search products..."
                                    value={search}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    onFocus={() => setDropdownOpen(true)}
                                    autoComplete="off"
                                />
                                {dropdownOpen && debouncedSearch.length >= 2 && (
                                    <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-mp-surface border border-mp-border rounded shadow-lg max-h-48 overflow-y-auto">
                                        {isSearching && (
                                            <div className="px-3 py-2 text-sm text-mp-muted">Searching...</div>
                                        )}
                                        {!isSearching && filteredResults.length === 0 && (
                                            <div className="px-3 py-2 text-sm text-mp-muted">No products found.</div>
                                        )}
                                        {filteredResults.map((p) => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-mp-background"
                                                onClick={() => handleSelectProduct(p)}
                                            >
                                                {p.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <Controller
                                    name="product_id"
                                    control={control}
                                    render={({ fieldState: { error } }) =>
                                        error ? <p className="text-mp-error text-xs mt-1">{error.message}</p> : <></>
                                    }
                                />
                            </div>

                            {attachMutation.isError && (
                                <Alert variant="error">
                                    <span>Failed to attach product.</span>
                                </Alert>
                            )}

                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    type="button"
                                    onClick={handleAttach}
                                    disabled={isSubmitting || attachMutation.isPending || !selectedProduct}
                                >
                                    {attachMutation.isPending ? 'Attaching...' : 'Attach Product'}
                                </Button>
                                <Button size="sm" variant="outline" type="button" onClick={handleCancel} disabled={attachMutation.isPending}>
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    ) : (
                        <div>
                            <h3 className="font-semibold mb-2">Add Product</h3>
                            <div className="flex items-start gap-4 justify-between">
                                <p className="text-sm text-mp-muted">
                                    Attach a product to this plan.
                                </p>
                                <Button
                                    size="sm"
                                    type="button"
                                    variant="primary"
                                    onClick={() => setShowForm(true)}
                                    className="whitespace-nowrap"
                                >
                                    Add Product
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </PermissionGuard>
        </div>
    );
};
